/**
 * Service Task Manager
 * 
 * Handles creation, grouping, and management of service tasks.
 * Service providers operate on tasks, not full orders.
 */

import { 
  collection, 
  doc, 
  setDoc, 
  writeBatch,
  query,
  where,
  getDocs,
  Timestamp,
  type DocumentData
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { CartItem } from '../contexts/CartContext';
import type { ServiceTask, TaskGroup, TaskStatus, PayoutStatus } from '../types/serviceTasks';

/**
 * Generate service tasks from cart items when order is placed
 */
export async function createServiceTasksFromOrder(
  orderId: string,
  customerId: string,
  customerName: string,
  cartItems: CartItem[],
  address: {
    fullName: string;
    phone: string;
    email: string;
    houseNumber: string;
    streetAddress: string;
    city: string;
    state: string;
    pincode: string;
    landmark?: string;
  },
  paymentTotal: number
): Promise<ServiceTask[]> {
  const batch = writeBatch(db);
  const tasks: ServiceTask[] = [];
  const now = new Date();
  
  // Platform takes 20% commission, provider gets 80%
  const PLATFORM_FEE_PERCENTAGE = 0.20;

  for (const item of cartItems) {
    // Generate unique task ID
    const taskId = `${orderId}_${item.id}_${item.date}_${item.time}_${Date.now()}`;
    
    // Parse time to create estimated start/end times
    const { startTime, endTime } = parseTimeSlot(item.date, item.time, item.duration);
    
    // Calculate payout (item price + addons price) * 80%
    const totalItemPrice = item.price + (item.addons?.reduce((sum, addon) => sum + addon.price, 0) || 0);
    const payoutAmount = totalItemPrice * (1 - PLATFORM_FEE_PERCENTAGE);
    const platformFee = totalItemPrice * PLATFORM_FEE_PERCENTAGE;

    // Create service task
    const task: ServiceTask = {
      taskId,
      orderId,
      serviceId: item.id,
      serviceName: item.name,
      serviceCategory: item.category || 'General',
      serviceDuration: parseDuration(item.duration),
      servicePrice: item.price,
      date: item.date,
      timeSlot: item.time,
      estimatedStartTime: startTime,
      estimatedEndTime: endTime,
      address: {
        fullName: address.fullName,
        phone: address.phone,
        email: address.email,
        fullAddress: `${address.houseNumber}, ${address.streetAddress}, ${address.landmark || ''}, ${address.city}, ${address.state} - ${address.pincode}`.replace(/,\s*,/g, ',').trim(),
        city: address.city,
        state: address.state,
        pincode: address.pincode,
      },
      providerId: null,
      status: 'pending',
      statusHistory: [{
        status: 'pending',
        timestamp: now,
        note: 'Task created from order'
      }],
      payoutAmount: Math.round(payoutAmount * item.quantity),
      payoutStatus: 'pending',
      platformFee: Math.round(platformFee * item.quantity),
      customerId,
      customerName,
      quantity: item.quantity,
      addons: item.addons?.map(addon => ({
        ...addon,
        duration: addon.duration ? parseDuration(addon.duration) : undefined
      })),
      createdAt: now,
      updatedAt: now,
    };

    // Find related tasks (same order, same location, same/nearby time)
    const relatedTaskIds = tasks
      .filter(t => 
        t.orderId === orderId && 
        t.date === task.date &&
        Math.abs(t.estimatedStartTime.getTime() - task.estimatedStartTime.getTime()) < 4 * 60 * 60 * 1000 // Within 4 hours
      )
      .map(t => t.taskId);
    
    if (relatedTaskIds.length > 0) {
      task.relatedTaskIds = relatedTaskIds;
      task.isGroupBooking = true;
      
      // Update related tasks to include this task
      tasks.forEach(t => {
        if (relatedTaskIds.includes(t.taskId)) {
          if (!t.relatedTaskIds) t.relatedTaskIds = [];
          t.relatedTaskIds.push(taskId);
        }
      });
    }

    tasks.push(task);

    // Add to Firestore batch
    const taskRef = doc(db, 'service_tasks', taskId);
    batch.set(taskRef, {
      ...task,
      estimatedStartTime: Timestamp.fromDate(task.estimatedStartTime),
      estimatedEndTime: Timestamp.fromDate(task.estimatedEndTime),
      createdAt: Timestamp.fromDate(task.createdAt),
      updatedAt: Timestamp.fromDate(task.updatedAt),
      statusHistory: task.statusHistory.map(h => ({
        ...h,
        timestamp: Timestamp.fromDate(h.timestamp)
      }))
    });
  }

  // Commit all tasks in batch
  await batch.commit();
  
  console.log(`Created ${tasks.length} service tasks for order ${orderId}`);
  return tasks;
}

/**
 * Group related tasks for smart provider suggestions
 */
export function groupRelatedTasks(tasks: ServiceTask[]): TaskGroup[] {
  const groups = new Map<string, ServiceTask[]>();

  // Group by order ID first
  tasks.forEach(task => {
    if (!groups.has(task.orderId)) {
      groups.set(task.orderId, []);
    }
    groups.get(task.orderId)!.push(task);
  });

  // Convert to TaskGroup objects
  const taskGroups: TaskGroup[] = [];

  groups.forEach((orderTasks, orderId) => {
    if (orderTasks.length === 0) return;

    // Sort by estimated start time
    orderTasks.sort((a, b) => 
      a.estimatedStartTime.getTime() - b.estimatedStartTime.getTime()
    );

    // Check if tasks can be grouped (compatible timings)
    const canBeGrouped = checkTaskCompatibility(orderTasks);
    
    // Calculate time gap between tasks
    let timeGap = 0;
    if (orderTasks.length > 1) {
      const firstEnd = orderTasks[0].estimatedEndTime.getTime();
      const secondStart = orderTasks[1].estimatedStartTime.getTime();
      timeGap = Math.round((secondStart - firstEnd) / (60 * 1000)); // minutes
    }

    taskGroups.push({
      orderId,
      customerId: orderTasks[0].customerId,
      tasks: orderTasks,
      address: orderTasks[0].address,
      date: orderTasks[0].date,
      totalDuration: orderTasks.reduce((sum, t) => sum + t.serviceDuration, 0),
      totalPayout: orderTasks.reduce((sum, t) => sum + t.payoutAmount, 0),
      canBeGrouped,
      timeGap: timeGap > 0 ? timeGap : undefined
    });
  });

  return taskGroups;
}

/**
 * Check if multiple tasks have compatible timings
 */
function checkTaskCompatibility(tasks: ServiceTask[]): boolean {
  if (tasks.length <= 1) return true;

  // Sort by start time
  const sorted = [...tasks].sort((a, b) => 
    a.estimatedStartTime.getTime() - b.estimatedStartTime.getTime()
  );

  // Check each pair of consecutive tasks
  for (let i = 0; i < sorted.length - 1; i++) {
    const current = sorted[i];
    const next = sorted[i + 1];
    
    const currentEnd = current.estimatedEndTime.getTime();
    const nextStart = next.estimatedStartTime.getTime();
    
    // Buffer time: 15 minutes between tasks (rest/travel/setup)
    const BUFFER_TIME_MS = 15 * 60 * 1000;
    
    // Check if there's enough gap
    if (nextStart < currentEnd) {
      return false; // Overlapping
    }
    
    // Check if gap is reasonable (not more than 3 hours)
    if (nextStart - currentEnd > 3 * 60 * 60 * 1000) {
      return false; // Too much gap
    }
  }

  return true;
}

/**
 * Parse duration string to minutes
 */
function parseDuration(duration: string): number {
  // Handle formats like "60 mins", "1 hour", "1.5 hours", etc.
  const hourMatch = duration.match(/(\d+(?:\.\d+)?)\s*(?:hour|hr)/i);
  if (hourMatch) {
    return Math.round(parseFloat(hourMatch[1]) * 60);
  }
  
  const minMatch = duration.match(/(\d+)\s*(?:min|minute)/i);
  if (minMatch) {
    return parseInt(minMatch[1], 10);
  }
  
  // Default to 60 minutes if can't parse
  return 60;
}

/**
 * Parse time slot and date to create Date objects
 */
function parseTimeSlot(date: string, time: string, duration: string): {
  startTime: Date;
  endTime: Date;
} {
  // Parse date (YYYY-MM-DD format)
  const [year, month, day] = date.split('-').map(Number);
  
  // Parse time (e.g., "10:00 AM" or "14:30")
  const timeMatch = time.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
  if (!timeMatch) {
    // Fallback to current time if parsing fails
    const now = new Date();
    return { startTime: now, endTime: new Date(now.getTime() + 60 * 60 * 1000) };
  }
  
  let hours = parseInt(timeMatch[1], 10);
  const minutes = parseInt(timeMatch[2], 10);
  const meridiem = timeMatch[3]?.toUpperCase();
  
  // Convert to 24-hour format
  if (meridiem === 'PM' && hours !== 12) {
    hours += 12;
  } else if (meridiem === 'AM' && hours === 12) {
    hours = 0;
  }
  
  const startTime = new Date(year, month - 1, day, hours, minutes, 0);
  const durationMinutes = parseDuration(duration);
  const endTime = new Date(startTime.getTime() + durationMinutes * 60 * 1000);
  
  return { startTime, endTime };
}

/**
 * Query tasks available for a specific provider
 */
export async function getAvailableTasksForProvider(
  providerId: string,
  providerSkills: string[],
  providerLocation: { city: string; state: string },
  targetDate?: string
): Promise<ServiceTask[]> {
  const tasksRef = collection(db, 'service_tasks');
  
  // Build query
  let q = query(
    tasksRef,
    where('status', '==', 'pending'),
    where('providerId', '==', null),
    where('serviceCategory', 'in', providerSkills.slice(0, 10)) // Firestore 'in' limit is 10
  );
  
  // Add date filter if provided
  if (targetDate) {
    q = query(q, where('date', '==', targetDate));
  }
  
  const snapshot = await getDocs(q);
  const tasks: ServiceTask[] = [];
  
  snapshot.forEach(doc => {
    const data = doc.data() as DocumentData;
    
    // Filter by location (same city/state)
    if (data.address.city === providerLocation.city || 
        data.address.state === providerLocation.state) {
      tasks.push({
        ...data,
        estimatedStartTime: data.estimatedStartTime.toDate(),
        estimatedEndTime: data.estimatedEndTime.toDate(),
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
        statusHistory: data.statusHistory.map((h: any) => ({
          ...h,
          timestamp: h.timestamp.toDate()
        }))
      } as ServiceTask);
    }
  });
  
  return tasks;
}

/**
 * Accept a task (or group of tasks)
 */
export async function acceptTask(
  taskId: string,
  providerId: string,
  providerName: string,
  acceptRelatedTasks: boolean = false
): Promise<{
  success: boolean;
  acceptedTaskIds: string[];
  message: string;
  totalEarnings: number;
}> {
  try {
    // Get the task
    const taskRef = doc(db, 'service_tasks', taskId);
    const taskSnap = await getDocs(query(collection(db, 'service_tasks'), where('taskId', '==', taskId)));
    
    if (taskSnap.empty) {
      return {
        success: false,
        acceptedTaskIds: [],
        message: 'Task not found',
        totalEarnings: 0
      };
    }

    const taskData = taskSnap.docs[0].data() as ServiceTask;
    
    // Check if task is still available
    if (taskData.status !== 'pending' || taskData.providerId !== null) {
      return {
        success: false,
        acceptedTaskIds: [],
        message: 'Task is no longer available',
        totalEarnings: 0
      };
    }

    const batch = writeBatch(db);
    const acceptedTaskIds: string[] = [taskId];
    let totalEarnings = taskData.payoutAmount;
    const now = new Date();

    // Update main task
    batch.update(doc(db, 'service_tasks', taskSnap.docs[0].id), {
      providerId,
      providerName,
      status: 'accepted',
      assignedAt: Timestamp.fromDate(now),
      updatedAt: Timestamp.fromDate(now),
      statusHistory: [
        ...taskData.statusHistory,
        {
          status: 'accepted',
          timestamp: Timestamp.fromDate(now),
          note: `Accepted by ${providerName}`
        }
      ]
    });

    // If accepting related tasks
    if (acceptRelatedTasks && taskData.relatedTaskIds && taskData.relatedTaskIds.length > 0) {
      const relatedQuery = query(
        collection(db, 'service_tasks'),
        where('taskId', 'in', taskData.relatedTaskIds.slice(0, 10))
      );
      
      const relatedSnap = await getDocs(relatedQuery);
      
      relatedSnap.forEach(relatedDoc => {
        const relatedData = relatedDoc.data() as ServiceTask;
        
        // Only accept if still pending
        if (relatedData.status === 'pending' && relatedData.providerId === null) {
          batch.update(doc(db, 'service_tasks', relatedDoc.id), {
            providerId,
            providerName,
            status: 'accepted',
            assignedAt: Timestamp.fromDate(now),
            updatedAt: Timestamp.fromDate(now),
            statusHistory: [
              ...relatedData.statusHistory,
              {
                status: 'accepted',
                timestamp: Timestamp.fromDate(now),
                note: `Accepted as part of group by ${providerName}`
              }
            ]
          });
          
          acceptedTaskIds.push(relatedData.taskId);
          totalEarnings += relatedData.payoutAmount;
        }
      });
    }

    await batch.commit();

    return {
      success: true,
      acceptedTaskIds,
      message: `Successfully accepted ${acceptedTaskIds.length} task(s)`,
      totalEarnings
    };
  } catch (error) {
    console.error('Error accepting task:', error);
    return {
      success: false,
      acceptedTaskIds: [],
      message: 'Failed to accept task',
      totalEarnings: 0
    };
  }
}

/**
 * Update task status
 */
export async function updateTaskStatus(
  taskId: string,
  newStatus: TaskStatus,
  note?: string
): Promise<boolean> {
  try {
    const taskQuery = query(
      collection(db, 'service_tasks'),
      where('taskId', '==', taskId)
    );
    
    const snapshot = await getDocs(taskQuery);
    
    if (snapshot.empty) {
      console.error('Task not found:', taskId);
      return false;
    }

    const taskDoc = snapshot.docs[0];
    const taskData = taskDoc.data() as ServiceTask;
    const now = new Date();

    await setDoc(doc(db, 'service_tasks', taskDoc.id), {
      ...taskData,
      status: newStatus,
      updatedAt: Timestamp.fromDate(now),
      statusHistory: [
        ...taskData.statusHistory,
        {
          status: newStatus,
          timestamp: Timestamp.fromDate(now),
          note: note || `Status changed to ${newStatus}`
        }
      ]
    }, { merge: true });

    return true;
  } catch (error) {
    console.error('Error updating task status:', error);
    return false;
  }
}
